import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { Alert, Button, Card, Field, PasswordInput } from '../components/ui';
import { useT } from '../i18n/locale';
import { useAdminSession } from './session';

/**
 * Shown instead of the admin area while must_change_password is set.
 *
 * Not a dismissible prompt: the bootstrap account is created from credentials
 * typed into an environment file, which tend to survive in deploy scripts and
 * chat logs. The server refuses every other admin route until this is done,
 * so offering a way past it would only produce confusing 403s.
 */
export default function ChangePassword({ forced }: { forced: boolean }) {
  const { refresh } = useAdminSession();
  const t = useT();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mismatch = confirm.length > 0 && confirm !== newPassword;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/platform/change-password', { currentPassword, newPassword });
      setDone(true);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('admin.changePwFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-md p-4">
      <Card className="p-6">
        <h2 className="m-0 text-base font-bold text-slate-900">{t('admin.changePwTitle')}</h2>
        {forced ? <Alert tone="warn">{t('admin.forcedNote')}</Alert> : null}
        {done ? <Alert tone="success">{t('admin.pwUpdated')}</Alert> : null}

        <form onSubmit={submit} className="mt-3 flex flex-col gap-3">
          {error ? <Alert tone="error">{error}</Alert> : null}
          <Field label={t('admin.currentPassword')}>
            <PasswordInput
              value={currentPassword}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
            />
          </Field>
          <Field label={t('common.newPassword')}>
            <PasswordInput
              value={newPassword}
              onChange={(e) => setNew(e.target.value)}
              autoComplete="new-password"
            />
          </Field>
          <Field
            label={t('admin.repeatNewPassword')}
            error={mismatch ? t('admin.mismatch') : undefined}
          >
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </Field>
          <Button
            type="submit"
            disabled={busy || !currentPassword || !newPassword || mismatch || !confirm}
          >
            {busy ? t('common.saving') : t('common.save')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
